import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLoginUser } from "@/hooks/useAuth";
import {
  changeNoticeKeys,
  fetchRosterChangeNotices,
  matchingChangeNotice,
} from "@/services/roster/changeNotices";

export function useRosterChangeNotice({
  groupId,
  year,
  month,
  snapshotId,
  refreshRoster,
  personal = false,
}: {
  readonly groupId: string;
  readonly year: number;
  readonly month: number;
  readonly snapshotId?: number | null;
  readonly refreshRoster: () => Promise<{ isError: boolean }>;
  readonly personal?: boolean;
}) {
  const user = useLoginUser();
  const key = changeNoticeKeys.month(
    user?.account_id ?? "",
    user?.office_id ?? "",
    user?.nurse_id ?? "",
    groupId,
    year,
    month
  );
  const query = useQuery({
    queryKey: key,
    queryFn: ({ signal }) =>
      fetchRosterChangeNotices(groupId, year, month, signal),
    enabled: !!user && !!groupId,
    refetchOnWindowFocus: true,
    refetchInterval: 30000,
  });
  const notices = query.isError ? [] : (query.data?.notices ?? []);
  const candidate = notices
    .filter((item) => item.status === "open")
    .sort((a, b) => b.notice_id - a.notice_id)[0];
  const refresh = useQuery({
    queryKey: [
      "roster-change-refresh",
      ...key,
      personal ? "personal" : "all",
      candidate?.notice_id,
    ],
    queryFn: async () => {
      const result = await refreshRoster();
      if (result.isError)
        throw new Error("변경된 근무표를 새로 불러오지 못했습니다.");
      return true;
    },
    enabled:
      !!candidate &&
      (personal || (snapshotId != null && snapshotId < candidate.notice_id)),
    staleTime: Infinity,
    refetchInterval: (refreshQuery) =>
      refreshQuery.state.status === "success" &&
      snapshotId != null &&
      candidate != null &&
      snapshotId < candidate.notice_id
        ? 5000
        : false,
    retry: false,
  });
  const scope = JSON.stringify(key);
  const [readyScope, setReadyScope] = useState<string | null>(null);
  const resolvingInitialChanges =
    query.isFetching || (personal && !!candidate && refresh.isPending);
  useEffect(() => {
    if (user && groupId && !resolvingInitialChanges) setReadyScope(scope);
  }, [user, groupId, resolvingInitialChanges, scope]);
  const olderNoticeRefreshes = useRef(new Set<string>());
  useEffect(() => {
    if (!candidate || snapshotId == null || snapshotId <= candidate.notice_id)
      return;
    const scope = JSON.stringify([...key, snapshotId, candidate.notice_id]);
    if (olderNoticeRefreshes.current.has(scope)) return;
    olderNoticeRefreshes.current.add(scope);
    void query.refetch();
  }, [candidate, snapshotId, key, query]);
  const notice =
    personal && !refresh.isSuccess
      ? undefined
      : matchingChangeNotice(notices, snapshotId);
  return {
    notice,
    isInitialLoading:
      !!user && !!groupId && readyScope !== scope && resolvingInitialChanges,
    isManager: query.data?.is_manager ?? false,
    isError: query.isError || refresh.isError,
    retry: async () => {
      await query.refetch();
      if (candidate) await refresh.refetch();
    },
  };
}
